
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'

type UseCaseOption = {
  text: string
  value: string
}

export type GenerateJigFormPayload = {
  description: string
  name: string
  useCase: string
  useCaseLabel: string
}

@Component
export default class GenerateJigFormFields extends Vue {
  private readonly minDescriptionLength: number = 10
  private readonly maxDescriptionLength: number = 2000

  @Prop({ type: Boolean, default: false })
  public disabled!: boolean

  private jigDescription: string = ''
  private jigName: string = ''
  private jigUseCase: string | null = ''
  private isFormValid: boolean = false

  private get useCaseOptions(): UseCaseOption[] {
    return [
      { text: 'Annotated diagram', value: 'tol-jig-annotated-diagram' },
      { text: 'Instructional guide', value: 'tol-jig-instructional-guide' },
      { text: 'Teaching module', value: 'tol-jig-teaching-module' },
      { text: 'Technical sales presentation', value: 'tol-jig-technical-sales-presentation' },
      { text: 'Trade show demo', value: 'tol-jig-trade-show-demo' },
    ]
  }

  private descriptionRules: Array<(v: string) => boolean | string> = [
    (v: string) => !!v || 'Description is required',
    (v: string) => (v && v.trim().length >= this.minDescriptionLength) || 'Description must be at least 10 characters',
    (v: string) => (!v || v.length <= this.maxDescriptionLength) || `Description must be ${this.maxDescriptionLength} characters or fewer`,
  ]

  private useCaseRules: Array<(v: string | null) => boolean | string> = [
    (v: string | null) => !!v || 'Use case is required',
  ]

  public get isValid(): boolean {
    if (this.disabled) {
      return false
    }
    const trimmedDescription = this.jigDescription ? this.jigDescription.trim() : ''
    const hasSelectedUseCase = this.getNormalizedUseCaseValue().length > 0
    return this.isFormValid &&
      hasSelectedUseCase &&
      trimmedDescription.length >= this.minDescriptionLength &&
      trimmedDescription.length <= this.maxDescriptionLength
  }

  @Watch('isValid', { immediate: true })
  private onValidChanged(next: boolean) {
    this.$emit('valid', next)
  }

  public getPayload(): GenerateJigFormPayload | null {
    if (!this.isValid) {
      return null
    }
    return {
      description: this.jigDescription,
      name: this.jigName,
      useCase: this.getNormalizedUseCaseValue(),
      useCaseLabel: this.getSelectedUseCaseLabel(),
    }
  }

  public reset() {
    this.jigDescription = ''
    this.jigName = ''
    this.jigUseCase = ''
    this.isFormValid = false
  }

  private getNormalizedUseCaseValue(): string {
    return this.jigUseCase ? this.jigUseCase.trim() : ''
  }

  private getSelectedUseCaseLabel(): string {
    const selectedUseCase = this.getNormalizedUseCaseValue()
    const selected = this.useCaseOptions.find((option: UseCaseOption) => option.value === selectedUseCase)
    return selected ? selected.text : ''
  }
}
